// SPDX-License-Identifier: AGPL-3.0-only
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { TOAST_DEFAULT_DURATIONS } from './Toast.js';
/** Max simultaneously visible toasts. */
export const MAX_VISIBLE_TOASTS = 4;
/** Auto-dismiss duration for toasts carrying an action (Undo/Download). */
export const TOAST_ACTION_DURATION = 5200;
/**
 * Pauses every timer bar's CSS countdown while the stack is hovered, keeping
 * the visual bar in sync with the paused JS timers. Passed to `ToastStack`
 * through `stackProps.className`.
 */
export const TOAST_HOVER_PAUSE_CLASS = '[&:hover_[data-testid=toast-timer-bar]]:[animation-play-state:paused]';
let toastSeq = 0;
function resolveDuration(options) {
    if (options.duration !== undefined)
        return options.duration;
    if (options.action !== undefined)
        return TOAST_ACTION_DURATION;
    return TOAST_DEFAULT_DURATIONS[options.variant ?? 'info'];
}
/**
 * useToastQueue — imperative toast queue manager: max 4 visible
 * newest-first, overflow queues FIFO and enters as older toasts dismiss,
 * per-variant auto-dismiss, pause-on-hover (JS timers + CSS timer bar),
 * undo-action toasts and promise toasts (loading → success/error). Mount
 * one queue per app next to a single `ToastStack`.
 */
export function useToastQueue() {
    const [toasts, setToasts] = useState([]);
    const visibleRef = useRef([]);
    const queueRef = useRef([]);
    const timersRef = useRef(new Map());
    const pausedRef = useRef(false);
    const flush = useCallback(() => {
        setToasts([...visibleRef.current]);
    }, []);
    const clearTimer = useCallback((id) => {
        const record = timersRef.current.get(id);
        if (record?.handle != null)
            clearTimeout(record.handle);
        timersRef.current.delete(id);
    }, []);
    // dismiss and startTimer are mutually recursive (timer fire → dismiss →
    // promote queued toast → start its timer); indirection via ref.
    const dismissRef = useRef(() => { });
    const startTimer = useCallback((id, duration) => {
        if (duration === null)
            return;
        const record = { handle: null, remaining: duration, startedAt: Date.now() };
        if (!pausedRef.current) {
            record.handle = setTimeout(() => dismissRef.current(id), duration);
        }
        timersRef.current.set(id, record);
    }, []);
    const dismiss = useCallback((id) => {
        if (id === undefined) {
            for (const toast of visibleRef.current)
                clearTimer(toast.id);
            visibleRef.current = [];
            queueRef.current = [];
            flush();
            return;
        }
        const index = visibleRef.current.findIndex((t) => t.id === id);
        if (index === -1) {
            queueRef.current = queueRef.current.filter((t) => t.id !== id);
            return;
        }
        clearTimer(id);
        visibleRef.current = visibleRef.current.filter((t) => t.id !== id);
        // Promote queued toasts FIFO into the freed slots (as the newest rows).
        while (queueRef.current.length > 0 && visibleRef.current.length < MAX_VISIBLE_TOASTS) {
            const next = queueRef.current.shift();
            visibleRef.current = [next, ...visibleRef.current];
            startTimer(next.id, next.duration ?? null);
        }
        flush();
    }, [clearTimer, flush, startTimer]);
    dismissRef.current = dismiss;
    const toItem = useCallback((id, options) => {
        const duration = resolveDuration(options);
        const action = options.action === undefined
            ? undefined
            : options.keepOnAction
                ? options.action
                : {
                    label: options.action.label,
                    onAction: () => {
                        options.action?.onAction();
                        dismissRef.current(id);
                    },
                };
        return {
            id,
            variant: options.variant,
            title: options.title,
            description: options.description,
            action,
            duration,
        };
    }, []);
    const push = useCallback((options) => {
        const id = options.id ?? `toast_${++toastSeq}`;
        const item = toItem(id, options);
        if (visibleRef.current.length >= MAX_VISIBLE_TOASTS) {
            queueRef.current = [...queueRef.current, item];
        }
        else {
            visibleRef.current = [item, ...visibleRef.current];
            startTimer(id, item.duration ?? null);
            flush();
        }
        return id;
    }, [flush, startTimer, toItem]);
    const update = useCallback((id, patch) => {
        const apply = (current) => toItem(id, {
            variant: 'variant' in patch ? patch.variant : current.variant,
            title: 'title' in patch ? patch.title : current.title,
            description: 'description' in patch ? patch.description : current.description,
            action: 'action' in patch ? patch.action : current.action,
            ...('duration' in patch ? { duration: patch.duration } : {}),
            ...(patch.keepOnAction !== undefined ? { keepOnAction: patch.keepOnAction } : {}),
        });
        const index = visibleRef.current.findIndex((t) => t.id === id);
        if (index !== -1) {
            const next = apply(visibleRef.current[index]);
            visibleRef.current = visibleRef.current.map((t) => (t.id === id ? next : t));
            clearTimer(id);
            startTimer(id, next.duration ?? null);
            flush();
            return;
        }
        queueRef.current = queueRef.current.map((t) => (t.id === id ? apply(t) : t));
    }, [clearTimer, flush, startTimer, toItem]);
    const promise = useCallback((input, messages) => {
        const id = push({ variant: 'loading', title: messages.loading, duration: null });
        input.then((value) => {
            update(id, {
                variant: 'success',
                title: typeof messages.success === 'function' ? messages.success(value) : messages.success,
                duration: TOAST_DEFAULT_DURATIONS.success,
            });
        }, (reason) => {
            update(id, {
                variant: 'error',
                title: typeof messages.error === 'function' ? messages.error(reason) : messages.error,
                duration: TOAST_DEFAULT_DURATIONS.error,
            });
        });
        return input;
    }, [push, update]);
    const pauseAll = useCallback(() => {
        if (pausedRef.current)
            return;
        pausedRef.current = true;
        const now = Date.now();
        for (const record of timersRef.current.values()) {
            if (record.handle !== null) {
                clearTimeout(record.handle);
                record.handle = null;
                record.remaining = Math.max(0, record.remaining - (now - record.startedAt));
            }
        }
    }, []);
    const resumeAll = useCallback(() => {
        if (!pausedRef.current)
            return;
        pausedRef.current = false;
        for (const [id, record] of timersRef.current.entries()) {
            record.startedAt = Date.now();
            record.handle = setTimeout(() => dismissRef.current(id), record.remaining);
        }
    }, []);
    useEffect(() => () => {
        for (const record of timersRef.current.values()) {
            if (record.handle !== null)
                clearTimeout(record.handle);
        }
        timersRef.current.clear();
    }, []);
    const stackProps = useMemo(() => ({
        toasts,
        onDismissToast: dismiss,
        onMouseEnter: pauseAll,
        onMouseLeave: resumeAll,
        className: TOAST_HOVER_PAUSE_CLASS,
    }), [toasts, dismiss, pauseAll, resumeAll]);
    return { toasts, push, dismiss, update, promise, pauseAll, resumeAll, stackProps };
}
//# sourceMappingURL=useToastQueue.js.map